import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, MessageCircle, Flame, Crown } from 'lucide-react';
import UltimateTag from '../components/UltimateTag';
import AdSlot from '../components/AdSlot';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzFBMUQyNCIvPjwvc3ZnPg==';

const TABS = [
  { id: 'rating', label: 'Top Rated',   icon: Star,          color: 'text-yellow-400', metricLabel: '★' },
  { id: 'chats',  label: 'Most Chats',  icon: MessageCircle, color: 'text-fuchsia-400', metricLabel: 'chats' },
  { id: 'streak', label: 'Top Streaks', icon: Flame,         color: 'text-orange-400', metricLabel: 'days' },
];

const PODIUM_STYLE = {
  1: { wrap: 'border-yellow-400 shadow-[0_0_30px_rgba(250,204,21,0.35)]', label: 'text-yellow-400', size: 'scale-110' },
  2: { wrap: 'border-violet-400', label: 'text-violet-300', size: '' },
  3: { wrap: 'border-blue-400',   label: 'text-blue-300',   size: '' },
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('rating');
  const [data, setData] = useState({ creators: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/leaderboard/creators`, { params: { category: tab, limit: 50 } })
      .then(({ data }) => setData(data))
      .catch(err => { console.error(err); setData({ creators: [] }); })
      .finally(() => setLoading(false));
  }, [tab]);

  const top3 = data.creators.slice(0, 3);
  const rest = data.creators.slice(3);
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean); // 2nd, 1st, 3rd

  const fmt = (c) => {
    if (tab === 'rating') return `${(c.avg_rating || c.metric || 0).toFixed(2)}★`;
    if (tab === 'chats')  return `${(c.total_chats || c.metric || 0).toLocaleString()} chats`;
    return `${c.streak_days || c.metric || 0} days`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0d29] via-[#0a0612] to-[#050505] text-white pb-24" data-testid="leaderboard-page">
      {/* Header */}
      <div
        className="sticky top-0 z-20 px-4 pb-3 flex items-center gap-2 bg-gradient-to-b from-[#1a0d29] to-transparent"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-white/70 hover:text-white rounded-full" data-testid="leaderboard-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 text-center">
          <h1 className="font-black text-lg tracking-tight font-['Unbounded']">Top Creators</h1>
        </div>
        <div className="w-9" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-4 pb-4 overflow-x-auto scrollbar-hide">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                active
                  ? 'bg-white text-black border-white'
                  : 'bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]'
              }`}
              data-testid={`leaderboard-tab-${t.id}`}
            >
              <Icon className={`w-4 h-4 ${active ? '' : t.color}`} />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="py-20 flex justify-center">
          <div className="w-7 h-7 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && data.creators.length === 0 && (
        <p className="py-16 text-center text-white/40 text-sm">No creators yet for this category.</p>
      )}

      {!loading && top3.length > 0 && (
        <>
          {/* Podium */}
          <div className="grid grid-cols-3 gap-2 px-4 pt-2 items-end">
            {podiumOrder.map((c) => {
              const rank = c.rank;
              const s = PODIUM_STYLE[rank];
              return (
                <Link
                  key={c.user_id}
                  to={`/user/${c.user_id}`}
                  className={`flex flex-col items-center text-center ${s.size}`}
                  data-testid={`podium-${rank}`}
                >
                  <div className={`relative aspect-square w-full rounded-2xl overflow-hidden border-2 ${s.wrap}`}>
                    <img
                      src={c.avatar || DEFAULT_AVATAR}
                      alt={c.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                    />
                    {rank === 1 && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Crown className="w-7 h-7 text-yellow-400 fill-yellow-400" />
                      </div>
                    )}
                  </div>
                  <p className={`mt-2 font-black text-base italic font-['Unbounded'] ${s.label}`}>No.{rank}</p>
                  <p className="font-bold text-sm text-white truncate max-w-full w-full">{c.name}</p>
                  {c.ultimate_tag && <div className="mt-1"><UltimateTag tag={c.ultimate_tag} size="xs" /></div>}
                  <p className="text-[10px] text-white/50">{c.character_count || 0} characters</p>
                  <p className="text-xs font-bold text-white/80 mt-1">{fmt(c)}</p>
                </Link>
              );
            })}
          </div>

          {/* Rest of the list */}
          <div className="px-4 mt-6 space-y-2">
            {rest.map((c) => (
              <Link
                key={c.user_id}
                to={`/user/${c.user_id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/15 transition-colors"
                data-testid={`leaderboard-row-${c.rank}`}
              >
                <span className="w-7 text-center text-base font-black text-white/40 font-['Unbounded']">{c.rank}</span>
                <img
                  src={c.avatar || DEFAULT_AVATAR}
                  alt={c.name}
                  className="w-12 h-12 rounded-lg object-cover border border-white/10"
                  onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-sm text-white truncate">{c.name}</p>
                    {c.ultimate_tag && <UltimateTag tag={c.ultimate_tag} size="xs" />}
                  </div>
                  <p className="text-[11px] text-white/50">{c.character_count || 0} characters</p>
                </div>
                <p className="text-sm font-bold text-white whitespace-nowrap">{fmt(c)}</p>
              </Link>
            ))}
          </div>
        </>
      )}
      <AdSlot variant="sticky" />
    </div>
  );
}
